import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppConfig } from 'src/app/models/AppConfig/AppConfig.model';
import { environment } from 'src/environments/environment';

const SESSION_KEY = 'app_config';
const PARTNER_KEY_STORAGE = 'app_partner_key';

const FALLBACK_CONFIG: AppConfig = {
  appName: 'FlipeOn',
  partnerKey: 'flipeon',
  supportEmail: 'suporte@flipeon.com.br',
  helpFallbackUrl: 'https://ajuda.flipeon.com/introducao/',
  websiteUrl: 'https://www.flipeon.com.br/',
  appBaseUrl: 'https://app.flipeon.com',
  downloadPdvPath: 'download/pdvflipeon',
  androidAppId: 'com.flipeon.flipeon',
  faviconUrl: 'assets/icons/app_icon.svg',
  logoUrl: 'assets/icons/flipeon_logo.png',
  primaryColor: '#27A5DF',
  estadosLiberadosImplantacao: null,
};

export { FALLBACK_CONFIG as FLIPEON_CONFIG };

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private config: AppConfig = this.loadFromSession() ?? FALLBACK_CONFIG;

  get isPartner(): boolean { return this.config.partnerKey !== 'flipeon'; }

  constructor(private http: HttpClient) {
    this.applyPrimaryColor();
  }

  get appName(): string { return this.config.appName; }
  get partnerKey(): string { return this.config.partnerKey; }
  get supportEmail(): string { return this.config.supportEmail; }
  get helpFallbackUrl(): string { return this.config.helpFallbackUrl; }
  get websiteUrl(): string { return this.config.websiteUrl; }
  get appBaseUrl(): string { return this.config.appBaseUrl; }
  get downloadPdvPath(): string { return this.config.downloadPdvPath; }
  get androidAppId(): string { return this.config.androidAppId; }
  get faviconUrl(): string { return this.config.faviconUrl; }
  get logoUrl(): string { return this.config.logoUrl; }
  get primaryColor(): string { return this.config.primaryColor || '#27A5DF'; }
  get estadosLiberadosImplantacao(): string[] | null { return this.config.estadosLiberadosImplantacao ?? null; }
  get hasEstadoRestriction(): boolean { return !!this.config.estadosLiberadosImplantacao?.length; }

  async initialize(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const partnerKey = params.get('p') ?? localStorage.getItem(PARTNER_KEY_STORAGE);

    if (!partnerKey) return; // nunca acessou com parceiro → fallback FlipeOn já aplicado no construtor

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: AppConfig }>(`${environment.apiUrl}/partner/app-config`, {
          params: { p: partnerKey }
        })
      );

      if (response?.success && response.data) {
        this.config = { ...response.data, partnerKey };
        this.saveToSession(this.config);
        localStorage.setItem(PARTNER_KEY_STORAGE, partnerKey);
        this.applyPrimaryColor();
      }
    } catch {
      // mantém config da sessão ou fallback FlipeOn
    }
  }

  setConfig(config: Partial<AppConfig>): void {
    this.config = { ...this.config, ...config };
    this.saveToSession(this.config);
    this.applyPrimaryColor();
  }

  applyPrimaryColor(): void {
    document.documentElement.style.setProperty('--primary-color', this.primaryColor);
  }

  private saveToSession(config: AppConfig): void {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(config));
  }

  private loadFromSession(): AppConfig | null {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AppConfig;
    } catch {
      return null;
    }
  }
}
